import { get } from "lodash";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UnitInputProps {
  label: string;
  name: string;
  type: string;
  maxLength?: number;
  placeholder?: string;
  readOnly?: boolean;
  numberOnly?: boolean;
  max?: number;
  customStyleClasses?: string;
  isRequired?: boolean;
  isFloat?: boolean;
}

const UnitInput: React.FC<UnitInputProps> = ({
  label,
  name,
  type,
  maxLength = 6,
  placeholder,
  readOnly = false,
  max,
  numberOnly = true,
  customStyleClasses = "",
  isRequired,
  isFloat,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const methods = useFormContext();
  const { t } = useTranslation();

  const [isFocused, setIsFocused] = useState(false);

  return (
    <div>
      <div className="bg-primary-bg relative flex items-center rounded-lg">
        <label htmlFor={name} className="sr-only">
          {label}
        </label>
        <div className="d flex-grow">
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              return (
                <div className="relative w-full max-w-[95dvw]">
                  <input
                    id={name}
                    type={numberOnly ? "number" : "text"}
                    inputMode="numeric"
                    min={1}
                    placeholder={placeholder || label}
                    readOnly={readOnly}
                    max={max}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const rawValue = e.target.value;

                      if (rawValue === "") {
                        field.onChange(null);
                        return;
                      }

                      let cleanValue = rawValue;

                      if (numberOnly) {
                        if (isFloat) {
                          cleanValue = rawValue.replace(/[^\d.]/g, "");

                          const parts = cleanValue.split(".");
                          if (parts.length > 2) {
                            cleanValue =
                              parts[0] + "." + parts.slice(1).join("");
                          }
                        } else {
                          cleanValue = rawValue.replace(/\D/g, "");
                        }
                      }

                      if (cleanValue === "") {
                        field.onChange(null);
                        return;
                      }

                      const trimmedValue =
                        cleanValue.length > maxLength
                          ? cleanValue.slice(0, maxLength)
                          : cleanValue;

                      if (numberOnly) {
                        field.onChange(
                          isFloat
                            ? parseFloat(trimmedValue)
                            : parseInt(trimmedValue, 10),
                        );
                      } else {
                        field.onChange(trimmedValue);
                      }
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                      field.onBlur();
                      setIsFocused(false);
                    }}
                    className={`border-primary-bg peer h-14 w-full rounded-lg border-2 bg-white px-3 pb-2 pt-2 focus:!border-primary focus:outline-none ${customStyleClasses} ${get(errors, name) ? "border-error" : ""} `}
                  />

                  {field?.value && (
                    <label
                      htmlFor={name}
                      className={`${isFocused ? "!text-primary" : ""} text-primary-text pointer-events-none absolute start-3 z-10 -translate-y-1/2 bg-white px-1 text-sm transition-all duration-200`}
                    >
                      {placeholder || label}
                    </label>
                  )}
                </div>
              );
            }}
          />
          <div className="absolute end-1 top-0 m-2 flex h-10 items-center rounded-lg bg-white px-3">
            <span>{t(type)}</span>
          </div>
        </div>
      </div>
      {isRequired && !methods.watch(name) && (
        <p className="mt-1 text-sm text-error">{t("required")}</p>
      )}
    </div>
  );
};

export default UnitInput;
